/**
 * Test Email — Roomgrid layout per the design handoff.
 *
 * Same frame as every channel test (TestPageShell): 780px card, sections
 * with micro headers, footer "Send test" CTA. Email's Message section adds
 * the subject, the rich editor and the attachments preview.
 */

import React, { useState } from "react";
import { FormProvider, Controller, useForm } from "react-hook-form";
import { Grid, MenuItem, TextField } from "@mui/material";
import { useNotify, useTranslate } from "react-admin";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import { sendTestNotification } from "../services/testService";
import { buildTestPayload } from "../transforms/buildTestPayload";
import {
  createEmptyTestFormValues,
  MESSAGE_PRIORITY_CHOICES,
} from "../constants";
import type { NotificationTestFormValues } from "../types/notificationTest";
import AccountAppPicker from "./sections/AccountAppPicker";
import RecipientsSection from "./sections/RecipientsSection";
import MessageSection from "./sections/MessageSection";
import SchedulingSection from "./sections/SchedulingSection";
import RgSection from "./sections/RgSection";
import TestPageShell from "./TestPageShell";

export const EmailTestPage: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);

  const methods = useForm<NotificationTestFormValues>({
    defaultValues: createEmptyTestFormValues(),
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
    shouldFocusError: true,
  });

  const onSubmit = async (
    values: NotificationTestFormValues,
  ): Promise<void> => {
    setSubmitting(true);
    try {
      const body = buildTestPayload("email", values);
      const response = await sendTestNotification("email", body);
      if (response.success) {
        notify("resources.test.notifications.emailSent", { type: "success" });
        methods.reset(createEmptyTestFormValues());
      } else {
        notify(
          response.message ??
            translate("resources.test.notifications.emailSendFailed"),
          { type: "error" },
        );
      }
    } catch (err: unknown) {
      notify(
        extractErrorMessage(
          err,
          "resources.test.notifications.emailSendFailed",
          translate,
        ),
        { type: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <TestPageShell
        titleKey="resources.test.email.title"
        submitting={submitting}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <RgSection
          label={translate("resources.test.sections.accountApplication", {
            _: "Account & application",
          })}
        >
          <AccountAppPicker />
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.message", {
            _: "Message",
          })}
        >
          <MessageSection channel="email" />
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.recipients", {
            _: "Recipients",
          })}
        >
          <RecipientsSection channel="email" />
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.scheduling", {
            _: "Scheduling",
          })}
        >
          <SchedulingSection />
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.delivery", {
            _: "Delivery options",
          })}
          last
        >
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} md={6}>
              <Controller
                name="messagePriority"
                control={methods.control}
                rules={{
                  required: translate(
                    "resources.test.validation.messagePriorityRequired",
                  ),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    required
                    label={translate("resources.test.fields.messagePriority")}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  >
                    {MESSAGE_PRIORITY_CHOICES.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {translate(c.labelKey)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="webhookUrl"
                control={methods.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={translate("resources.test.fields.webhookUrl")}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </RgSection>
      </TestPageShell>
    </FormProvider>
  );
};

export default EmailTestPage;
